/**
 * When a control's focus outline is drawn (RESERVATIONS_V2_PLAN.md decision 128, RV5f-3 L12).
 *
 * react-native-web's `Pressable` reports `focused` after a MOUSE press too, because the browser focuses a button when it is
 * clicked. A look that paints an outline for `focused` therefore leaves a ring on a button the pointer just pressed. A
 * member that takes `focusOutline` lets a screen choose: `"always"` (the default, what every member drew before) or
 * `"keyboard"`, the outline only when the focus came from the keyboard. Only the Reservations screens opt in; no other v2
 * screen changes. `index.desktop.ts` and `index.native.ts` say how each platform tells the two apart.
 */
import type { PressState } from "@rista/ui/library/components/PressableSurface";

/**
 * When the focus outline shows: `"always"` whenever the control is focused (the default), or `"keyboard"` only when the
 * focus came from the keyboard, never after a press with the pointer or a finger.
 */
export type FocusOutline = "always" | "keyboard";

/** What a platform's helper must answer: whether the last input was the keyboard. */
export type LastInputWasKeyboard = () => boolean;

/**
 * The press state with `focused` cleared when `mode` is `"keyboard"` and the last input was not the keyboard; the state
 * unchanged for `"always"` or when nothing is given.
 */
export function stateForOutline(mode: FocusOutline | undefined, state: PressState, lastInputWasKeyboard: LastInputWasKeyboard): PressState {
  if (mode !== "keyboard" || !state.focused || lastInputWasKeyboard()) {
    return state;
  }
  return { ...state, focused: false };
}
